// Copyright 2024 praveen.konathala
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { initializeApp, getApps, getApp } from 'firebase/app';
import 'firebase/auth'; // Import Firebase services as needed
import { getAuth } from 'firebase/auth';
import 'firebase/database';
import {
  Database,
  equalTo,
  get,
  getDatabase,
  orderByChild,
  push,
  query,
  ref,
  remove,
  set,
  update,
} from 'firebase/database';
import { AppConfig } from 'src/config/AppConfig';
import { User } from 'src/types/user';

//export const firebseApp = initializeApp(AppConfig.firebaseApp);
//Default app (if not already initialized)
export const firebseApp =
  getApps().find(app => app.name === '[DEFAULT]') || initializeApp(AppConfig.firebaseApp);

// Named app for ZoomShops
export const zoomShopsFirebaseApp =
  getApps().find(app => app.name === 'zoomShopsApp') ||
  initializeApp(AppConfig.zoomShopsFirebaseApp, 'zoomShopsApp');

export const brandsDb = getDatabase(firebseApp, AppConfig.brands_db_url);
export const locationsDb = getDatabase(firebseApp, AppConfig.locations_db_url);
export const servicerAppDb = getDatabase(
  firebseApp,
  AppConfig.servicer_app_db_url,
);
export const userAppDb = getDatabase(firebseApp, AppConfig.user_db_url);

export const nmsdashDb = getDatabase(firebseApp, AppConfig.nmsdash_db_url);
const zoomShopsApp = getApp('zoomShopsApp');
export const zoomShopsDb = getDatabase(zoomShopsApp, AppConfig.zoom_shops_db_url);

export const authentication = getAuth(firebseApp);
export const checkActiveUser = async (userUid: string) => {
  const userRef = ref(userAppDb, `activeUsers/${userUid}`);

  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      return snapshot.val(); // Return the data
    } else {
      return null; // No data found
    }
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error; // Handle error
  }
};

export const createNewActiveUser = async (userData: User, uid: string) => {
  const activeUsersRef = ref(userAppDb, `activeUsers/${uid}`); // Reference to activeUsers

  try {
    // Push a new child node under 'activeUsers' and generate a unique ID
    const data = {
      ...userData,
      createdAt: new Date().toISOString(),
    };

    console.log('saving ----', data);
    // Set the data at the generated reference
    await set(activeUsersRef, data);

    console.log('User created successfully with ID:', activeUsersRef.key);
    return userData; // Return the generated user ID
  } catch (error) {
    console.error('Error creating new user:', error);
    return null;
  }
};

export const removeUserInvitation = async (id: string) => {
  const activeUserRef = ref(userAppDb, `userInvitations/${id}`); // Reference to the specific active user node

  try {
    // Remove the user node from the database
    await remove(activeUserRef);

    console.log(`User with ID ${id} removed successfully.`);
  } catch (error) {
    console.error('Error removing user:', error);
    throw error;
  }
};

export const getAllActiveUsers = async () => {
  const userRef = ref(userAppDb, `activeUsers`);

  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      return snapshot.val(); // Return the data
    } else {
      return null; // No data found
    }
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error; // Handle error
  }
};

export const getInvitation = async (userUid: string) => {
  const userRef = ref(userAppDb, `userInvitations/${userUid}`);

  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      return snapshot.val(); // Return the data
    } else {
      return null; // No data found
    }
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error; // Handle error
  }
};

const getBrands = () => {
  return get(ref(brandsDb, `brands`));
};

const getBrandsConfig = () => {
  return get(ref(brandsDb, `bq_config`));
};
const getBrandsSecurity = () => {
  return get(ref(brandsDb, `security`));
};

const getNmsDashConfig = () => {
  return get(ref(nmsdashDb, `bq_config`));
};

export const getNmsDashAlerts = () => {
  return get(ref(zoomShopsDb, `nms`));
};

// Update a specific role in Firebase
export const updateRole = async (
  db: Database,
  roleId: string,
  updatedData: Partial<any>,
) => {
  const rolePath = `/security/roles/${roleId}`;
  const roleRef = ref(db, rolePath);

  try {
    await update(roleRef, updatedData);
    console.log(`Role ${roleId} updated successfully.`);
  } catch (error) {
    console.error(`Failed to update role ${roleId}:`, error);
  }
};

// Add a new role to Firebase
export const addNewRole = async (db: Database, newRoleData: any) => {
  const rolesRef = ref(db, `/security/roles`);

  try {
    const newRoleRef = push(rolesRef); // Create a new key
    await update(newRoleRef, newRoleData); // Add the new role data
    console.log('New role added successfully:', newRoleRef.key);
  } catch (error) {
    console.error('Failed to add new role:', error);
  }
};

export const addnewUser = async (
  newRoleData: any,
  isExistingInviteID: string,
) => {
  if (newRoleData) {
    newRoleData['appVersion'] = 'dashv2';
  }
  const path =
    isExistingInviteID.length > 0
      ? `/userInvitations/${isExistingInviteID}`
      : `/userInvitations`;
  try {
    if (isExistingInviteID) {
      // **Update existing invitation**
      const existingInviteRef = ref(userAppDb, path);
      await update(existingInviteRef, newRoleData);
      console.log('Existing invite updated successfully:', isExistingInviteID);
    } else {
      // **Create new invitation**
      const rolesRef = ref(userAppDb, path);
      const newRoleRef = push(rolesRef); // Generate new unique ID
      await set(newRoleRef, newRoleData); // Use `set` to replace data at that node
      console.log('New invite added successfully:', newRoleRef.key);
    }
  } catch (error) {
    console.error('Failed to add new user:', error);
  }
};

export const editUserRoles = async (
  newUserData: any,
  uuId: string,
  userType: string,
  clientType: string,
) => {

  let node: string | undefined;
  let specNode: string | undefined;

  if (userType === "external") {
    if (clientType === "Brand") {
      node = `brands/brandList`;
      specNode = 'brandList';
    } else if (clientType === "Locations") {
      node = `locations/locationList`;
      specNode = 'locationList';
    }
  } else {
    if (clientType === "Brand") {
      node = `brands/brandCommonRoles`;
      specNode = 'brandCommonRoles';
    } else if (clientType === "Locations") {
      node = `locations/locationCommonRoles`;
      specNode = 'locationCommonRoles';
    }
  }

  if (!specNode || !node) {
    console.error("Invalid specNode or node detected.");
    return;
  }

  // Ensure newUserData contains the expected key before accessing it
  if (!(specNode in newUserData)) {
    console.error(`Missing data: newUserData.${specNode} is undefined.`);
    return;
  }

  const path = `/activeUsers/${uuId}/dash/${node}`;
  const updates = { [path]: newUserData[specNode] };

  try {
    await update(ref(userAppDb), updates);
    console.log(`Userroles updated successfully.`);
  } catch (error) {
    console.error(`Failed to update role:`, error);
  }
};

export const updateClientLastOption = async (
  userId: string,
  clientType: string,
  updatedData: string,
) => {
  const node = clientType == 'Brand' ? 'activeBrand' : 'activeLocationPartner';
  const path = `/activeUsers/${userId}/dash/${node}`;
  const updates = { [path]: updatedData };

  try {
    await update(ref(userAppDb), updates);
    console.log(`ClientType ${updatedData} updated successfully.`);
  } catch (error) {
    console.error(`Failed to update role ${updatedData}:`, error);
  }
};

export const updateBrandsForUser = async (
  userId: string,
  updatedData: string,
) => {
  const path = `/activeUsers/${userId}/dash/brands`;
  const updates = { [path]: updatedData };

  try {
    await update(ref(userAppDb), updates);
    console.log(`ClientType ${updatedData} updated successfully.`);
  } catch (error) {
    console.error(`Failed to update role ${updatedData}:`, error);
  }
};

export const updateLocationForUser = async (
  userId: string,
  updatedData: string,
) => {
  const path = `/activeUsers/${userId}/dash/locations`;
  const updates = { [path]: updatedData };

  try {
    await update(ref(userAppDb), updates);
    console.log(`ClientType ${updatedData} updated successfully.`);
  } catch (error) {
    console.error(`Failed to update role ${updatedData}:`, error);
  }
};

export const updateClientType = async (userId: string, clientType: string) => {
  const path = `/activeUsers/${userId}/dash/activeType`;
  const updates = { [path]: clientType };

  try {
    await update(ref(userAppDb), updates);
    console.log(`ClientType ${clientType} updated successfully.`);
  } catch (error) {
    console.error(`Failed to update role ${clientType}:`, error);
  }
};

export const getLocations = () => {
  return get(ref(locationsDb, `locations`));
};

const getLocationsConfig = () => {
  return get(ref(locationsDb, `bq_config`));
};
const getLocationsSecurity = () => {
  return get(ref(locationsDb, `security`));
};

export const getServicerAppSecurity = () => {
  return get(ref(servicerAppDb, `security`));
};

export const getClientTypes = (type: string) => {
  //if (type === 'Brand') {
  if (type === 'Brand' || type === 'NMS') {
    return getBrands();
  }
  return getLocations();
};

export const getClientConfig = (type: string) => {
  if (type === 'Brand') {
    return getBrandsConfig();
  }
  else if ( type === 'NMS') {
    return getNmsDashConfig();
  } 
  else {
  return getLocationsConfig();
  }
};

export const getClientSecurity = (type: string) => {
  if (type === 'Brand') {
    return getBrandsSecurity();
  }
  return getLocationsSecurity();
};

export const checkEmailExistsInInvitation = async (
  db: Database,
  email: string,
): Promise<string> => {
  const userInvitationsRef = ref(db, '/userInvitations');
  const emailQuery = query(
    userInvitationsRef,
    orderByChild('email'),
    equalTo(email),
  );

  try {
    const snapshot = await get(emailQuery);

    if (snapshot.exists()) {
      console.log(`Email found: ${email}`);
      return Object.keys(snapshot.val())[0] || '';
      //return true;
    } else {
      console.log(`Email not found: ${email}`);
      return '';
    }
  } catch (error) {
    console.error('Error checking email:', error);
    return '';
  }
};

export const checkEmailExistsActiveUSers = async (
  db: Database,
  email: string,
): Promise<string> => {
  const userInvitationsRef = ref(db, '/activeUsers');
  const emailQuery = query(
    userInvitationsRef,
    orderByChild('email'),
    equalTo(email),
  );

  try {
    const snapshot = await get(emailQuery);

    if (snapshot.exists()) {
      console.log(`Email found: ${email}`);
      return Object.keys(snapshot.val())[0] || '';
    } else {
      console.log(`Email not found: ${email}`);
      return '';
    }
  } catch (error) {
    console.error('Error checking email:', error);
    return '';
  }
};

interface RowData {
  email: string;
  [key: string]: any;
}

export const deleteUserByEmail = async (
  row: RowData,
  activeTab: number,
  callback: (error: Error | null, success: boolean) => void,
): Promise<void> => {
  const userRef = ref(userAppDb, '/activeUsers');
  const emailQuery = query(userRef, orderByChild('email'), equalTo(row.email));

  let organization = '';
  switch (activeTab) {
    case 0:
      organization = 'brands';
      break;
    case 2:
      organization = 'locations';
      break;
    case 4:
      organization = 'servicerApp';
      break;
    default:
      organization = 'unknown';
      break;
  }

  try {
    const snapshot = await get(emailQuery);

    if (snapshot.exists()) {
      snapshot.forEach(async (childSnapshot) => {
        const key = childSnapshot.key;

        if (key) {
          const dashRef = ref(userAppDb, `/activeUsers/${key}/dash`);
          const dashSnapshot = await get(dashRef);

          if (dashSnapshot.exists()) {
            const allNodes = Object.keys(dashSnapshot.val());

            const validOrganizations = ['brands', 'locations', 'servicerApp'];
            const existingOrganizations = allNodes.filter((node) =>
              validOrganizations.includes(node),
            );

            let recordRef: any = null;

            if (existingOrganizations.length === 1) {
              // Remove the entire user if only one organization exists
              recordRef = ref(userAppDb, `/activeUsers/${key}`);
            } else {
              switch (organization) {
                case 'brands':
                  recordRef = ref(userAppDb, `/activeUsers/${key}/dash/brands`);
                  break;
                case 'locations':
                  recordRef = ref(
                    userAppDb,
                    `/activeUsers/${key}/dash/locations`,
                  );
                  break;
                case 'servicerApp':
                  recordRef = ref(
                    userAppDb,
                    `/activeUsers/${key}/dash/servicerApp`,
                  );
                  break;
                case 'unknown':
                  recordRef = ref(userAppDb, `/activeUsers/${key}`);
                  break;
                default:
                  callback(
                    new Error(`Invalid organization type: ${organization}`),
                    false,
                  );
                  return;
              }
            }

            // Await the remove operation
            await remove(recordRef);
            console.log(`User with email ${row.email} deleted successfully.`);
            callback(null, true);
          } else {
            console.log(`No dash data found for user with email ${row.email}`);
            callback(
              new Error(`No dash data found for user with email ${row.email}`),
              false,
            );
          }
        }
      });
    } else {
      console.log(`No user found with email ${row.email}`);
      callback(new Error(`No user found with email ${row.email}`), false);
    }
  } catch (error) {
    console.error('Error deleting user by email:', error);
    callback(error, false);
  }
};

// Enable or Disable alert in Firebase
export const updateAlertStatus = async (
  alertId: string,
  updatedData: number,
) => {
  //const alertRef = ref(nmsdashDb, `nms/alerts/${alertId}`);
  const alertRef = ref(zoomShopsDb, `nms/alerts/${alertId}`);
  try {
    await update(alertRef, { enabled: updatedData });
    console.log(`Alert status of ${alertId} updated successfully.`);
  } catch (error) {
    console.error(`Failed to update Alert Status of ${alertId}:`, error);
  }
};
